import { formatDate } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { AfasProject } from 'src/app/afas-classes/afas-project';
import { AfasUser } from 'src/app/afas-classes/afas-user';
import { Employees } from 'src/app/afas-classes/employees.class';
import { Hourtype } from 'src/app/afas-classes/hourtype';
import { PtRealization } from 'src/app/afas-classes/ptrealization.class';
import { Realization } from 'src/app/afas-classes/realization.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AfasAuthService } from 'src/app/services/afas-auth.service';
import { AfasService } from 'src/app/services/afas.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';

@Component({
    selector: 'app-profile-afas',
    templateUrl: './profile-afas.component.html',
    standalone: false,
})
export class ProfileAfasComponent extends CodaltComponent implements OnInit, OnDestroy {
    relForm: FormGroup<ControlsOf<PtRealization>>;

    employees: Employees[];
    realization: Realization[];
    hourtypes: Hourtype[];
    afasUser: AfasUser;
    requestOtpError: string = null;
    validateOtpError: string = null;
    user: string;
    otp: string;
    environmentCode: string;
    environmentTest: string;
    result: Object = {};
    projects: AfasProject[];
    materieel: Employees[];
    otpRequest = false;
    otpRequesting = false;
    otpRequested = false;
    otpValidating = false;
    loadingRealization = false;
    savingRealization = false;
    fcEmployee: FormControl<string>;

    constructor(
        private authService: AfasAuthService,
        private afasService: AfasService,
        private confirmDialogService: ConfirmDialogService,
        private elRef: ElementRef
    ) {
        super();
    }

    ngOnInit() {
        this.loadProfile();
        this.fcEmployee = new FormControl<string>();
        this.subscriptions.add(
            this.fcEmployee.valueChanges.subscribe(() => {
                this.loadRealization();
            })
        );
    }

    loadProfile() {
        this.subscriptions.add(
            this.afasService.getProfile().subscribe(profile => {
                this.afasUser = profile.afas_user;
                this.environmentCode = profile.afas_environment;
                this.environmentTest = profile.afas_test;
                this.createRealizationForm();
                this.loadAfas();
                this.loadRealization();
            })
        );
    }

    loadAfas() {
        this.subscriptions.add(
            this.afasService.getProjects().subscribe(projectResponse => {
                this.projects = projectResponse.data?.filter(p => !!p.hoofdproject);
            })
        );

        this.subscriptions.add(
            this.afasService.getEmployees().subscribe(projectResponse => {
                this.employees = projectResponse.data;
            })
        );

        this.subscriptions.add(
            this.afasService.getMaterieel().subscribe(materieelResponse => {
                this.materieel = materieelResponse.data;
            })
        );
        this.subscriptions.add(
            this.afasService.getHourtypes().subscribe(hourtypes => {
                this.hourtypes = hourtypes.data;
            })
        );
    }

    loadRealization() {
        this.loadingRealization = true;
        this.realization = [];
        this.subscriptions.add(
            this.afasService.getRealization(this.fcEmployee?.value).subscribe(
                emp => {
                    this.loadingRealization = false;
                    this.realization = emp.data;
                },
                () => {
                    this.loadingRealization = false;
                }
            )
        );
    }

    deleteTokens() {
        this.confirmDialogService
            .confirm({
                title: 'Koppeling met Afas verwijderen',
                text: 'Weet je zeker dat je de koppeling van Afas aan de planning account wilt verwijderen?',
                confirm: 'Ontkoppelen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.authService.deleteExisting().then(() => {
                        this.loadProfile();
                    });
                },
                () => {}
            );
    }

    // When username does not exists, Afas still replies success
    requestOtp() {
        this.requestOtpError = null;
        this.otpRequesting = true;

        this.authService.requestOtp(this.environmentCode + '.' + this.user).then(
            data => {
                if (data['response'] == 201) {
                    this.otpRequested = true;
                    this.otpRequest = false;
                    this.result = data;
                    setTimeout(() => {
                        let a = this.elRef.nativeElement.querySelector(`#passwordField`);
                        a.focus();
                    }, 250);
                } else {
                    this.requestOtpError = JSON.stringify(data);
                }
            },
            data => {
                if (data.data && data.data.errormessage) {
                    this.requestOtpError = data.data.errormessage;
                } else {
                    this.requestOtpError = JSON.stringify(data);
                }
            }
        );
    }

    // Request OTP (one-time-password) based on the Afas Profit Username
    // Will send an OTP to the user by e-mail if username excists and permissions avaiable

    // Validate the entered OTP (one-time-password) by retrieving the user that is authenticating
    validateOtp() {
        this.validateOtpError = null;
        this.otpValidating = true;

        this.authService.validateOtp(this.environmentCode + '.' + this.user, this.otp).then(
            data => {
                if (data['response'] == 201) {
                    this.result = data;
                    this.loadProfile();
                    this.reset();
                    this.otpRequest = false;
                } else {
                    this.otpValidating = false;
                    // Invalid password
                    if (data['response'] == 204) {
                        this.validateOtpError = 'Dit wachtwoord is niet juist.';
                    } else {
                        this.validateOtpError = JSON.stringify(data);
                    }
                }
            },
            data => {
                this.otpValidating = false;
                if (data.data && data.data.errormessage) {
                    this.validateOtpError = data.data.errormessage;
                } else {
                    this.validateOtpError = JSON.stringify(data);
                }
            }
        );
    }

    // Reset complete process so you can start over
    reset() {
        this.otpRequest = true;
        this.otpRequested = false;
        this.otpRequesting = false;
        this.validateOtpError = null;
        this.requestOtpError = null;
        this.user = null;
        this.otp = null;
        this.result = {};
    }

    createRealizationForm() {
        this.relForm = new FormGroup<ControlsOf<PtRealization>>({
            DaTi: new FormControl<string>(formatDate(new Date(), 'yyyy-MM-dd', 'nl')),
            VaIt: new FormControl('1'),
            ItCd: new FormControl('GW'), // ItemCode
            StId: new FormControl('1'),
            PrId: new FormControl(),
            EmId: new FormControl(this.afasUser?.personId),
            Ds: new FormControl('test via codalt'),
            Qu: new FormControl(7.25),
        });
    }

    saveRealization() {
        this.savingRealization = true;
        this.afasService.saveRealization(this.relForm.value as PtRealization).subscribe(
            () => {
                this.savingRealization = false;
                this.loadRealization();
            },
            () => {
                this.savingRealization = false;
            }
        );
    }
}
